<template>
	<span class="title-list-sorting-switcher" @click.prevent="openSelection">
		<span v-if="selectedItem" class="title-list-sorting-switcher__label">{{ $t('WEBAPP_SORTING_BY') }}</span>
		<span v-if="selectedItem?.translationKey">{{ $t(selectedItem.translationKey) }}</span>
		<FontAwesomeIcon :icon="faChevronDown" />
	</span>
</template>

<script setup lang="ts">
import { computed } from 'vue';

import TitleListSortingSwitchPopover from '@/components/list/sorting/TitleListSortingSwitchPopover.vue';

import { CollectionType } from '@/enums/collection-type';
import { TitleOrder } from '@/enums/title-order';

import { defaultSortingItemSelectedKeys, sortingItemsByCollectionType } from '@/helpers/list-sorting-helper';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';

import { isProWebCountry } from '@/helpers/pro-web-helper';
import { PopoverHelper } from '@/helpers/vfm/popover-helper';
import { getSelectedTab } from '@/components/popular/provider-module/helper';
import { useFilterStore } from '@/stores/pinia/useFilterStore';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { storeToRefs } from 'pinia';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useRoutingStore } from '@/stores/pinia/useRoutingStore';
import { useUserStore } from '@/stores/pinia/useUserStore';

const props = defineProps<{
	collectionType: CollectionType;
}>();

const { filters } = useFilterStore();
const { webLocale } = storeToRefs(useLanguageStore());
const { queryParams, activeRoute } = storeToRefs(useRoutingStore());
const { showFreemium } = storeToRefs(useUserStore());

const defaultSortingItemKey = computed(() => defaultSortingItemSelectedKeys[props.collectionType]);

const supportsProWeb = computed(() => isProWebCountry(webLocale.value));

const sortingItems = computed(() => {
	if (!supportsProWeb.value && !showFreemium.value) {
		return sortingItemsByCollectionType[props.collectionType].filter(item => !item.premium);
	}
	return sortingItemsByCollectionType[props.collectionType];
});

const sortByFilter = computed<TitleOrder>(() => {
	if (queryParams.value.sort_by) {
		const hasSortingItem = sortingItems.value.some(item => item.key === queryParams.value.sort_by);
		return hasSortingItem ? (queryParams.value.sort_by as TitleOrder) : defaultSortingItemKey.value;
	}
	return (filters().sort_by as TitleOrder) || defaultSortingItemKey.value;
});

const selectedItem = computed(() => sortingItems.value.find(item => item.key === sortByFilter.value));

const openSelection = (event: Event) => {
	const routeName: string = activeRoute.value?.name as string;
	const selectedTab = getSelectedTab(routeName);

	TrackingHelper.trackEvent('userinteraction', {
		action: 'sorting_dropdown_clicked',
		property: selectedTab,
	});

	PopoverHelper.create({
		popoverProps: {
			cssClass: 'static-pages-popover',
			boundingElement: event.currentTarget,
		},
		component: TitleListSortingSwitchPopover,
	});
};
</script>

<style lang="scss">
@import '~@/styles/variables';

.title-list-sorting-switcher {
	display: flex;
	align-items: center;
	color: $jw-color-lightgray--dark !important;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	cursor: pointer;
	font-size: 14px;
	font-weight: 700;

	&__label {
		margin-inline-end: 4px;
		font-size: 14px;
		font-weight: 700;

		// hide label for smallest devices
		@media (max-width: 320px) {
			display: none;
		}
	}
}

.title-list-sorting-switch-action-sheet {
	.action-sheet-premium-disabled {
		opacity: 0.6;
	}
}
</style>
